import { useState, useEffect } from 'react';
import { X, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SOURCE_SYSTEMS = [
  'SharePoint',
  'OneDrive', 
  'Google Drive',
  'Microsoft 365',
  'Addepar',
  'InfoTrack',
  'Praemium',
  'Xplan',
  'Class Super',
  'BGL',
  'Other'
];

const CATEGORIES = [
  'Charter',
  'Policy',
  'Minutes',
  'Register',
  'Planning',
  'Framework',
  'Constitution',
  'Agreement',
  'Other'
];

export default function GovernanceModal({ document, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    sourceSystem: '',
    owner: '',
    lastUpdated: new Date().toISOString().split('T')[0]
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title || '',
        category: document.category || '',
        sourceSystem: document.sourceSystem || '',
        owner: document.owner || '',
        lastUpdated: document.lastUpdated || new Date().toISOString().split('T')[0]
      });
    }
  }, [document]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.sourceSystem) newErrors.sourceSystem = 'Source system is required';
    if (!formData.owner.trim()) newErrors.owner = 'Owner is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  const isEditing = !!document;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in" 
      data-testid="governance-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="governance-modal-title"
      aria-describedby="governance-modal-description"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E8E4DF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#4A3B32]" />
            </div>
            <div>
              <h2 id="governance-modal-title" className="font-heading text-lg text-[#2C2420]">
                {isEditing ? 'Edit Document' : 'Add New Document'}
              </h2>
              <p id="governance-modal-description" className="text-sm text-[#5D524D]">
                {isEditing ? 'Update document details.' : 'Add a governance document to this client.'}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="hover:bg-[#F0EBE6]"
            data-testid="close-governance-modal-btn"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              Title *
            </label>
            <Input
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter document title"
              className={errors.title ? 'border-red-500' : ''}
              data-testid="governance-title-input"
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.title}
              </p>
            )}
          </div>

          {/* Category & Source System */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Category *
              </label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger className={errors.category ? 'border-red-500' : ''} data-testid="governance-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.category}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Source System *
              </label>
              <Select value={formData.sourceSystem} onValueChange={(v) => handleChange('sourceSystem', v)}>
                <SelectTrigger className={errors.sourceSystem ? 'border-red-500' : ''} data-testid="governance-source-select">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_SYSTEMS.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.sourceSystem && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.sourceSystem}
                </p>
              )}
            </div>
          </div>

          {/* Owner & Last Updated */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Owner *
              </label>
              <Input
                value={formData.owner}
                onChange={(e) => handleChange('owner', e.target.value)}
                placeholder="Document owner"
                className={errors.owner ? 'border-red-500' : ''}
                data-testid="governance-owner-input"
              />
              {errors.owner && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.owner}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Last Updated
              </label>
              <Input
                type="date"
                value={formData.lastUpdated}
                onChange={(e) => handleChange('lastUpdated', e.target.value)}
                data-testid="governance-date-input"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#E8E4DF] bg-[#F9F7F5]">
          <Button variant="outline" onClick={onClose} className="caffeine-btn-secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="caffeine-btn" data-testid="save-governance-btn">
            {isEditing ? 'Save Changes' : 'Add Document'}
          </Button>
        </div>
      </div>
    </div>
  );
}
